import { useCallback, useEffect, useState } from "react";
import type { MaterialDto, ProductDto, WorkCenterDto } from "@grizzly/shared";
import { formatAmount } from "../lib/format.js";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { useI18n } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { WorkCenterDialog } from "../components/catalog/workCenterDialog.js";
import { MaterialDialog } from "../components/catalog/materialDialog.js";
import { ProductDialog } from "../components/catalog/productDialog.js";
import { ProductRevisionDialog } from "../components/catalog/productRevisionDialog.js";

interface CatalogState {
  workCenters: WorkCenterDto[];
  materials: MaterialDto[];
  products: ProductDto[];
  error: string | null;
}

export function CatalogPage() {
  const { t } = useI18n();
  const [state, setState] = useState<CatalogState>({
    workCenters: [],
    materials: [],
    products: [],
    error: null,
  });
  const [wcOpen, setWcOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [revising, setRevising] = useState<ProductDto | null>(null);

  const load = useCallback(async () => {
    try {
      const [wc, mat, prod] = await Promise.all([
        api.listWorkCenters(),
        api.listMaterials(),
        api.listProducts(),
      ]);
      setState({
        workCenters: wc.items,
        materials: mat.items,
        products: prod.items,
        error: null,
      });
    } catch (err) {
      const e = err as ApiClientError;
      setState((s) => ({ ...s, error: e.message }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const workCenterColumns: Column<WorkCenterDto>[] = [
    { key: "code", header: t("catalog.column.code"), render: (r) => r.code },
    {
      key: "name",
      header: t("catalog.column.name"),
      render: (r) => <span className="font-medium text-ink">{r.name}</span>,
    },
    {
      key: "rate",
      header: t("catalog.column.hourlyRate"),
      render: (r) => <span className="tabular">{formatAmount(r.hourlyRateMinor)}</span>,
    },
  ];

  const materialColumns: Column<MaterialDto>[] = [
    { key: "sku", header: t("catalog.column.sku"), render: (r) => r.sku },
    { key: "name", header: t("catalog.column.name"), render: (r) => r.name },
    { key: "unit", header: t("catalog.column.unit"), render: (r) => r.unitOfMeasure },
    {
      key: "cost",
      header: t("catalog.column.standardCost"),
      render: (r) => <span className="tabular">{formatAmount(r.standardCostMinor)}</span>,
    },
  ];

  const productColumns: Column<ProductDto>[] = [
    { key: "code", header: t("catalog.column.code"), render: (r) => r.code },
    {
      key: "name",
      header: t("catalog.column.name"),
      render: (r) => (
        <span className="font-medium text-ink">
          {r.name}
          <span className="ms-2 text-xs text-ink-muted">{r.kind}</span>
        </span>
      ),
    },
    {
      key: "revision",
      header: t("catalog.column.revision"),
      render: (r) =>
        r.latestRevisionId ? (
          <Badge tone="ready">{t("catalog.revision.released")}</Badge>
        ) : (
          <Badge tone="neutral">{t("catalog.revision.noRevision")}</Badge>
        ),
    },
    {
      key: "actions",
      header: t("catalog.column.actions"),
      render: (r) => (
        <Button size="sm" variant="secondary" onClick={() => setRevising(r)}>
          {t("catalog.revision.addRevision")}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("catalog.title")}
        description={t("catalog.description")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setWcOpen(true)}>
              {t("catalog.buttons.workCenter")}
            </Button>
            <Button variant="secondary" onClick={() => setMaterialOpen(true)}>
              {t("catalog.buttons.material")}
            </Button>
            <Button variant="primary" onClick={() => setProductOpen(true)}>
              {t("catalog.buttons.product")}
            </Button>
          </>
        }
      />

      {state.error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {state.error}
        </p>
      ) : null}

      <div className="space-y-6">
        <section>
          <h2 className="mb-2 text-sm font-semibold text-ink">
            {t("catalog.sections.workCenters")}
          </h2>
          <DataTable
            caption={t("catalog.sections.workCenters")}
            rows={state.workCenters}
            rowKey={(r) => r.id}
            columns={workCenterColumns}
          />
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold text-ink">
            {t("catalog.sections.materials")}
          </h2>
          <DataTable
            caption={t("catalog.sections.materials")}
            rows={state.materials}
            rowKey={(r) => r.id}
            columns={materialColumns}
          />
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold text-ink">
            {t("catalog.sections.products")}
          </h2>
          <DataTable
            caption={t("catalog.sections.products")}
            rows={state.products}
            rowKey={(r) => r.id}
            columns={productColumns}
          />
        </section>
      </div>

      <WorkCenterDialog
        open={wcOpen}
        onOpenChange={setWcOpen}
        onCreated={() => void load()}
      />
      <MaterialDialog
        open={materialOpen}
        onOpenChange={setMaterialOpen}
        onCreated={() => void load()}
      />
      <ProductDialog
        open={productOpen}
        onOpenChange={setProductOpen}
        onCreated={() => void load()}
      />
      {revising ? (
        <ProductRevisionDialog
          productId={revising.id}
          open
          onOpenChange={(open) => {
            if (!open) setRevising(null);
          }}
          onCreated={() => void load()}
        />
      ) : null}
    </>
  );
}
